import { useState } from "react";
import { Pencil, Star, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Panel, inputCls, labelCls, btnPrimary, btnGhost, iconBtn } from "@/admin/ui";

const EMPTY = { name: "", city: "", rating: 5, comment: "" };

const Stars = ({ value, onChange, size = 18 }) => (
  <div className="flex gap-0.5" data-testid={onChange ? "review-stars" : undefined}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={onChange ? `review-star-${n}` : undefined}>
        <Star size={size} className={n <= value ? "fill-[#FFCC00] text-[#FFCC00]" : "text-slate-300"} />
      </button>
    ))}
  </div>
);

export const ReviewsPanel = ({ items, onAdd, onUpdate, onDelete }) => {
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const ok = form.name.trim() && form.comment.trim();

  const submit = async () => {
    if (!ok) return;
    const payload = { ...form, rating: Number(form.rating) };
    if (editingId) await onUpdate(editingId, payload);
    else await onAdd(payload);
    setForm(EMPTY);
    setEditingId(null);
  };

  return (
    <Panel id="avaliacoes" title="Avaliações" subtitle={`${items.length} avaliação(ões) · aparecem em "O que dizem nossos hóspedes"`} testId="reviews-panel">
      <div className="grid md:grid-cols-5 gap-5">
        <div className="md:col-span-2 space-y-3 rounded-3xl bg-[#FBF8F3] border border-[#EDE6DA] p-4">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Hóspede</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="review-name" />
            </div>
            <div>
              <label className={labelCls}>Cidade/UF</label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="review-city" />
            </div>
          </div>
          <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={22} />
          <textarea placeholder="O que o hóspede disse..." value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
            className={`${inputCls} h-28 py-2`} data-testid="review-comment" />
          <div className="flex gap-2">
            <button onClick={submit} disabled={!ok} className={`${btnPrimary} flex-1`} data-testid="review-save">{editingId ? "Salvar alterações" : "Adicionar avaliação"}</button>
            {editingId && <button onClick={() => { setEditingId(null); setForm(EMPTY); }} className={btnGhost} data-testid="review-cancel">Cancelar</button>}
          </div>
        </div>
        <div className="md:col-span-3 space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {items.length === 0 && <p className="text-sm text-slate-500 text-center py-10">Nenhuma avaliação ainda.</p>}
          {items.map((r) => (
            <div key={r.id} className={`rounded-2xl border border-slate-200 p-4 ${r.active ? "" : "opacity-50"}`} data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-full bg-[#0A66C2]/10 text-[#0A66C2] font-bold flex items-center justify-center shrink-0">{r.name[0]?.toUpperCase()}</div>
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 text-sm truncate">{r.name} <span className="text-slate-400 font-normal">{r.city}</span></div>
                  <Stars value={r.rating} size={13} />
                </div>
                <div className="ml-auto flex items-center gap-1">
                  <Switch className="data-[state=checked]:bg-[#0A66C2] data-[state=unchecked]:bg-slate-200 [&>span]:bg-white" checked={r.active} onCheckedChange={(v) => onUpdate(r.id, { active: v })} data-testid={`review-toggle-${r.id}`} />
                  <button className={`${iconBtn} hover:text-[#0A66C2]`} onClick={() => { setEditingId(r.id); setForm({ name: r.name, city: r.city, rating: r.rating, comment: r.comment }); }} data-testid={`review-edit-${r.id}`}><Pencil size={14} /></button>
                  <button className={`${iconBtn} hover:text-red-600`} onClick={() => window.confirm(`Apagar a avaliação de ${r.name}?`) && onDelete(r.id)} data-testid={`review-delete-${r.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-2 line-clamp-3">"{r.comment}"</p>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
};
